import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ConsentStatus = 'pending' | 'accepted' | 'rejected';

interface ConsentContextValue {
  status: ConsentStatus;
  analyticsAllowed: boolean;
  accept: () => void;
  reject: () => void;
  withdraw: () => void;
}

const STORAGE_KEY = 'fe.consent';

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

function readStored(): ConsentStatus {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'accepted' || raw === 'rejected') return raw;
  } catch {
    // SSR / private-mode / storage-disabled fallthrough
  }
  return 'pending';
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<ConsentStatus>(readStored);

  // Keep in sync if another tab changes the value.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setStatus(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<ConsentContextValue>(() => ({
    status,
    analyticsAllowed: status === 'accepted',
    accept: () => {
      localStorage.setItem(STORAGE_KEY, 'accepted');
      setStatus('accepted');
    },
    reject: () => {
      localStorage.setItem(STORAGE_KEY, 'rejected');
      setStatus('rejected');
    },
    withdraw: () => {
      localStorage.removeItem(STORAGE_KEY);
      setStatus('pending');
    },
  }), [status]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const v = useContext(ConsentContext);
  if (!v) throw new Error('useConsent must be used within ConsentProvider');
  return v;
}
